import { Button, Popover, Tooltip } from '@mui/material';
import '../ConditionalFormatting.css';
import { Dispatch, useEffect, useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { connect, useSelector } from 'react-redux';
import {
  addTableConditionalFormats,
  deleteTablecf,
  updatecfObjectOptions,
  updateConditionalFormatName,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import ShortUniqueId from 'short-unique-id';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LabelComponent from './LabelComponent';
import GradientComponent from './GradientComponent';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RuleComponent from './RuleComponent';
import {
  getContrastColor,
  getLabelValues,
  displayName,
  getUidOfKey,
  getNameFromUid,
  checkIfMissingColumn,
  getColumnFromUid,
} from '../../CommonFunctions/CommonFunctions';
import { palette } from '../../..';
import { RootState } from '../../../redux';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { useMemo } from 'react';
import { getMinMaxForFormattedData } from '../../Charts/CrossTab/ConditionalColorFormat';
import { ChartDataFieldProps } from '../../Charts/ChartsCommonInterfaces';

export const addConditionButtonStyle = {
  backgroundColor: 'rgb(43, 185, 187)',
  height: '25px',
  width: '100%',
  color: 'white',
  textTransform: 'none',
  marginLeft: '4px',
  '&:hover': {
    backgroundColor: 'rgb(43, 185, 187)',
  },
};

const subMenuItemStyle = {
  textTransform: 'none',
  color: 'grey',
  display: 'block',
};

interface Props {
  chartControls: any;
  tabTileProps: any;
  chartProperties: any;
  token: any;
  chartGroup: any;

  addTableConditionalFormats: (propKey: string, item: any) => void;
  updateConditionalFormatName: (propKey: string, name: string, index: number) => void;
  updatecfObjectOptions: (propKey: string, removeIndex: number, item: any) => void;
  deleteTablecf: (propKey: string, index: number) => void;
  iconColor?: string;
  backgroundColor?: string;
}

const TableConditionalFormatting = ({
  //state
  chartControls,
  tabTileProps,
  chartProperties,
  token,
  chartGroup,

  //dispatch
  addTableConditionalFormats,
  updateConditionalFormatName,
  updatecfObjectOptions,
  deleteTablecf,
  iconColor,
  backgroundColor,
}: Props) => {
  var propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  var uId = new ShortUniqueId({ length: 8 });

  const combinedThemes = useCombinedColorSchemes();
  const tabId = useSelector((state: RootState) => state.tabTileProps.selectedTabId);
  const defaultThemeId = useSelector(
    (state: RootState) => state.tabState.tabs[tabId]?.dashboardState.defaultThemeId,
  );

  const defaultThemeName = useMemo(() => {
    let theme = defaultThemeId
      ? combinedThemes.find((t) => t.id === defaultThemeId || t.themeId === defaultThemeId)
      : undefined;

    if (!theme && combinedThemes.length > 0) {
      theme = combinedThemes[0];
    }

    return theme?.name;
  }, [defaultThemeId, combinedThemes]);

  const [anchorEl, setAnchorEl] = useState<any>();
  const [optionAnchorEl, setOptionAnchorEl] = useState<any>();
  const [selectedColumnName, setSelectedColumnName] = useState<string>('');
  const [selectedColumnId, setSelectedColumnId] = useState<string>('');
  const [menu, setMenu] = useState<any>([]);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [openSubMenu, setOpenSubMenu] = useState<boolean>(false);
  ////const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const [gradientMinAndMax, setGradientMinAndMax] = useState<any>({
    min: 0,
    max: 0,
  });

  useEffect(() => {
    if (chartControls.properties[propKey]?.tableConditionalFormats?.length > 0) {
      const index = chartControls.properties[propKey].tableConditionalFormats.findIndex(
        (format: any) => format.columnId === selectedColumnId,
      );
      const format = chartControls.properties[propKey].tableConditionalFormats[index];
      const newName = getNameFromUid(
        chartProperties.properties[propKey].chartAxes,
        format?.columnId,
        index,
      );
      updateConditionalFormatName(propKey, newName, index);
      setSelectedColumnName(newName);
    }
    //eslint-disable-next-line
  }, [chartProperties.properties[propKey].chartAxes]);

  const _getLabelValues = async (columnName: string) => {
    try {
      return getLabelValues(
        columnName,
        chartControls,
        chartProperties,
        propKey,
        token,
        defaultThemeName,
        combinedThemes,
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getMinAndMaxValue = (column: string) => {
    const valuesArray = chartControls.properties[propKey].chartData.map((el: any) => {
      return el[column];
    });
    const minValue = Number(Math.min(...valuesArray)).toFixed(2);
    const maxValue = Number(Math.max(...valuesArray)).toFixed(2);

    const minMaxValues = getMinMaxForFormattedData(minValue, maxValue);

    const finalMinValue = Number(minValue) < 1000 ? minValue : minMaxValues.formattedMinInNumber;
    const finalMaxValue = Number(maxValue) < 1000 ? maxValue : minMaxValues.formattedMaxInNumber;
    setGradientMinAndMax({
      min: finalMinValue,
      max: finalMaxValue,
    });

    return { min: minMaxValues.formattedMinInNumber, max: minMaxValues.formattedMaxInNumber };
  };

  const onSelectOption = (option: string) => {
    // if the selected format type for the selected column is rule
    if (option === 'rule') {
      const columnId = getUidOfKey(
        chartProperties.properties[propKey].chartAxes,
        selectedColumnName,
      );
      addTableConditionalFormats(propKey, {
        id: uId(),
        isLabel: false,
        isGradient: false,
        name: selectedColumnName,
        columnId: columnId,
        isCollapsed: false,
        value: [],
      });
      setSelectedColumnId(columnId);
    }

    // if the selected format type for the selected column is Gradient
    if (option === 'gradient') {
      const columnId = getUidOfKey(
        chartProperties.properties[propKey].chartAxes,
        selectedColumnName,
      );
      // here we adding 3 values as default , for min,max and NULL values
      addTableConditionalFormats(propKey, {
        id: uId(),
        isLabel: false,
        isGradient: true,
        name: selectedColumnName,
        columnId: columnId,
        isCollapsed: false,
        value: [
          {
            id: uId(),
            forNull: false,
            name: 'Min',
            // getting minimum and maximum values of the selected column
            value: getMinAndMaxValue(selectedColumnName).min,
            isBold: false,
            isItalic: false,
            isUnderlined: false,
            backgroundColor: '#AF99DB',
            fontColor: getContrastColor('#2BB9BB'),
          },

          {
            id: uId(),
            forNull: false,
            name: 'Max',
            // getting minimum and maximum values of the selected column
            value: getMinAndMaxValue(selectedColumnName).max,
            isBold: false,
            isItalic: false,
            isUnderlined: false,
            backgroundColor: '#2BB9BB',
            fontColor: getContrastColor('#D87A80'),
          },
          {
            id: uId(),
            forNull: true,
            name: 'Null',
            value: 'null',
            isBold: false,
            isItalic: false,
            isUnderlined: false,
            backgroundColor: '#D87A80',
            fontColor: getContrastColor('#AF99DB'),
          },
        ],
      });
      setSelectedColumnId(columnId);
    }
  };

  // when select any column from dimension
  const onSelectColumn = async (column: any) => {
    var canAdd = false;
    /* checking length of array to check whether this item is already exist or not, if the lenth 
    is zero then this is the first item else check the array with columnname */
    if (chartControls.properties[propKey].tableConditionalFormats.length === 0) {
      canAdd = true;
    } else {
      var columnAlreadyExist = chartControls.properties[propKey].tableConditionalFormats.filter(
        (item: any) => item.name === column.columnName,
      )[0];
      if (!columnAlreadyExist) {
        canAdd = true;
      }
    }

    /* if the is is not already exist or this is the first item */
    if (canAdd) {
      if (column.isLabel) {
        let _colValues = column.isLabel ? await _getLabelValues(column.columnName) : '';
        const columnId = getUidOfKey(
          chartProperties.properties[propKey].chartAxes,
          column.columnName,
        );
        addTableConditionalFormats(propKey, {
          isLabel: column.isLabel,
          name: column.columnName,
          columnId: columnId,
          // getLabelValues - function to get all values of selected column
          value: _colValues,
          isCollapsed: false,
          backgroundColor: 'white',
          fontColor: 'black',
          isItalic: false,
          isUnderlined: false,
          isBold: false,
        });
        setSelectedColumnId(columnId);
        setOpenMenu(false);
      } else {
        // if the column is not from dimension (means it is from measure zone) , open the popover with two options gradient , rule
        setOpenSubMenu(true);
      }
    }
  };

  // useEffect(()=>{
  // 	setIsUpdate(!isUpdate);
  // },
  // [chartControls.properties[propKey].tableConditionalFormats]);

  // getting the list of columns with isLabel key (it is true if it is a dimension column else it will be false) whenever the chartData changes
  useEffect(() => {
    if (chartControls.properties[propKey].chartData.length > 0) {
      const zones = chartProperties.properties[propKey].chartAxes;

      const output = zones.reduce((accumulator: any, zone: any) => {
        if (zone.name !== 'Column' && zone.name !== 'Filter') {
          accumulator = accumulator.concat(
            zone.fields.map((value: any) => ({
              isLabel: zone.name !== 'Measure',
              columnName: displayName(value),
            })),
          );
        }
        return accumulator;
      }, []);

      setMenu(output);
    }
  }, [chartControls.properties[propKey].chartData]);

  useEffect(() => {
    if (chartControls.properties[propKey]?.tableConditionalFormats?.length > 0) {
      let columnDetails: ChartDataFieldProps[] = [];
      chartControls.properties[propKey]?.tableConditionalFormats.forEach((format: any) => {
        const column = getColumnFromUid(
          chartProperties.properties[propKey].chartAxes,
          format.columnId,
        );
        if (column) {
          columnDetails.push(column);
        }
      });

      if (columnDetails.length > 0) {
        const selectedDs = chartProperties.properties[propKey].selectedDs;
        columnDetails.map(async (column: any) => {
          const res = await checkIfMissingColumn(
            column,
            selectedDs,
            tabTileProps.tablesForSelectedDataSets[selectedDs.id],
          );
          // console.log(res);
          if (res.isMissing) {
            const has = chartControls.properties[propKey]?.tableConditionalFormats.find(
              (format: any) => format.columnId === column.uId,
            );
            // console.log(has);
            if (has) {
              deleteTablecf(propKey, has.index);
            }
          }
        });
      }
    }
  }, []);

  return (
    <div className="optionsInfo">
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: '0px',
          paddingRight: '0.5rem',
        }}
      >
        {chartControls.properties[propKey].tableConditionalFormats &&
          chartControls.properties[propKey].tableConditionalFormats.map(
            (format: any, i: number) => {
              return (
                <>
                  <div
                    style={{
                      width: '100%',
                      borderBottom: '2px solid rgb(224,224,224)',
                      paddingBottom: '0.2rem',
                    }}
                  >
                    <div className="ColumnTitle">
                      <span
                        style={{
                          fontSize: '12px',
                          color: iconColor ? iconColor : palette.primary.contrastText,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 'calc(100% - 50px)',
                          display: 'inline-block',
                        }}
                      >
                        {getNameFromUid(
                          chartProperties.properties[propKey].chartAxes,
                          format.columnId,
                        )}
                      </span>
                      <span
                        className="deleteIconContainer"
                        style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}
                      >
                        <Tooltip
                          title="Reset"
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '12px',
                              },
                            },
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            className="lucide lucide-eraser-icon lucide-eraser"
                            color={iconColor ? iconColor : 'currentColor'}
                            onClick={() => deleteTablecf(propKey, i)}
                          >
                            <path d="M21 21H8a2 2 0 0 1-1.42-.587l-3.994-3.999a2 2 0 0 1 0-2.828l10-10a2 2 0 0 1 2.829 0l5.999 6a2 2 0 0 1 0 2.828L12.834 21" />
                            <path d="m5.082 11.09 8.828 8.828" />
                          </svg>
                        </Tooltip>
                      </span>
                      <span
                        className="expandLessMoreContainer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: 'unset',
                          fontSize: '18px',
                        }}
                      >
                        {format.isCollapsed ? (
                          <ChevronRightIcon
                            sx={{
                              fontSize: '16px',
                              color: iconColor,
                            }}
                            onClick={() => {
                              updatecfObjectOptions(propKey, i, {
                                ...format,
                                isCollapsed: false,
                              });
                            }}
                          />
                        ) : (
                          <ExpandMoreIcon
                            sx={{
                              fontSize: '16px',
                              color: iconColor,
                            }}
                            onClick={() => {
                              updatecfObjectOptions(propKey, i, {
                                ...format,
                                isCollapsed: true,
                              });
                            }}
                          />
                        )}
                      </span>
                    </div>
                    {!format.isCollapsed ? (
                      <>
                        {format.isLabel ? (
                          <LabelComponent format={format} iconColor={iconColor} />
                        ) : (
                          <>
                            {format.isGradient ? (
                              <>
                                <GradientComponent
                                  gradientMinMax={gradientMinAndMax}
                                  format={format}
                                  iconColor={iconColor}
                                />
                              </>
                            ) : (
                              <RuleComponent
                                format={format}
                                i={i}
                                iconColor={iconColor}
                                backgroundColor={backgroundColor}
                              />
                            )}
                          </>
                        )}
                      </>
                    ) : null}
                  </div>
                </>
              );
            },
          )}
      </div>
      <div style={{ width: '98%', paddingRight: '2px', marginTop: '0px', paddingTop: '8px' }}>
        <Button
          sx={addConditionButtonStyle}
          disabled={chartControls.properties[propKey].chartData.length > 0 ? false : true}
          onClick={(e: any) => {
            setAnchorEl(e.currentTarget);
            setOpenMenu(true);
          }}
        >
          Add
        </Button>
      </div>
      {chartControls.properties[propKey].chartData.length > 0 ? null : (
        <p style={{ color: '#ccc', fontStyle: 'italic', fontSize: '10px' }}>
          *Create a chart first and then add conditions*
        </p>
      )}
      <Popover
        open={openMenu}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        onClose={() => setOpenMenu(false)}
      >
        {menu &&
          menu.map((column: any, index: number) => {
            return (
              <div
                className="menuItemStyle"
                style={{
                  borderBottom:
                    index === menu.length - 1 ? 'none' : '1px solid rgba(224,224,224,1)',
                  cursor: chartControls.properties[propKey].tableConditionalFormats.filter(
                    (item: any) => item.name === column.columnName,
                  )[0]
                    ? 'not-allowed'
                    : '',
                  padding: '8px 8px',
                  lineHeight: '1.2',
                  height: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  maxWidth: '250px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                onClick={(e) => {
                  onSelectColumn(column);
                  setOptionAnchorEl(e.currentTarget);
                  setSelectedColumnName(column.columnName);
                }}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.columnName}
                </span>
                {column.isLabel ? null : (
                  <span
                    style={{
                      float: 'right',
                      marginLeft: '5px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <KeyboardArrowRightIcon />
                  </span>
                )}
              </div>
            );
          })}
      </Popover>

      <Popover
        open={openSubMenu}
        anchorEl={optionAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        onClose={() => setOpenSubMenu(false)}
      >
        <Button
          sx={subMenuItemStyle}
          value="gradient"
          onClick={(e: any) => {
            setOpenSubMenu(false);
            setOpenMenu(false);
            onSelectOption(e.target.value);
          }}
        >
          Gradient
        </Button>
        <Button
          sx={subMenuItemStyle}
          value="rule"
          onClick={(e: any) => {
            setOpenSubMenu(false);
            setOpenMenu(false);
            onSelectOption(e.target.value);
          }}
        >
          Rule
        </Button>
      </Popover>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
    chartProperties: state.chartProperties,
    token: state.isLogged.accessToken,
    chartGroup: state.chartFilterGroup,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    addTableConditionalFormats: (propKey: string, item: any) =>
      dispatch(addTableConditionalFormats(propKey, item)),
    updateConditionalFormatName: (propKey: string, name: string, index: number) =>
      dispatch(updateConditionalFormatName(propKey, name, index)),
    updatecfObjectOptions: (propKey: string, removeIndex: number, item: any) =>
      dispatch(updatecfObjectOptions(propKey, removeIndex, item)),
    deleteTablecf: (propKey: string, index: number) => dispatch(deleteTablecf(propKey, index)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TableConditionalFormatting);
